import styled from '@emotion/styled';
import { Badge, Link, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { FilterKindEnum } from '../../../../domains/global-filters/model/FilterChain';
import { getLabels } from '../../../../utils/LabelFormatter/SingleLabelFormatter/ErrorLabelFormatter';
import { GenericFilter } from '../GenericBloc/GenericFilter/GenericFilter';
import { GenericFilterFooterMessage } from '../GenericBloc/GenericFilter/GenericFilterFooterMessage';
import { ErrorFilterDropdown } from './ErrorFilterDropdown';
import { getFilterMetadata } from '../../FilterChainUtils';
import type { DateRange, MinimalDateConstraint } from '../DateRangeFilter/DateRangeFilterType';
import type { AbstractSingleFilterPillsProps } from '../SingleFilterType';
import type { ErrorType } from './ErrorFilterType';

interface ErrorFilterProps extends AbstractSingleFilterPillsProps<ErrorType> {
  dateConstraint?: MinimalDateConstraint;
  dateRange?: DateRange;
  isScaleTrial?: boolean;
}

const StyledLink = styled(Link)`
  color: rgba(0, 0, 0, 0.6);
`;

const FooterContainer = styled(GenericFilterFooterMessage)`
  display: flex;
  align-items: center;
`;

const FooterText = styled.span`
  color: rgba(0, 0, 0, 0.6);
  margin-left: ${tokens.spacer.x2};
`;

export function ErrorFilter({
  autoFocus,
  currentValue,
  onApply,
  onRemove,
  onDropdownHidden,
  showDropdownOnMount,
  dateConstraint,
  dateRange,
  isScaleTrial = false,
}: ErrorFilterProps): JSX.Element {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const metadata = getFilterMetadata(FilterKindEnum.Error);
  const { label1, label2 } = getLabels(currentValue);
  const handleApply = (close: () => void, newValue?: ErrorType) => {
    onApply(newValue);
    close();
  };

  return (
    <GenericFilter
      autoFocus={autoFocus}
      filterName={metadata.filterName}
      label1={label1}
      label2={label2}
      iconName={metadata.iconName}
      onRemove={onRemove}
      showDropdownOnMount={showDropdownOnMount}
      onDropdownHidden={onDropdownHidden}
      renderDropdown={({ close }) => {
        return (
          <ErrorFilterDropdown
            filterName={metadata.filterName}
            currentValue={currentValue}
            onApply={(newValue) => handleApply(close, newValue)}
            header={
              <span>
                {t('common:recordings.error-filter.description')}
                &nbsp;
                <StyledLink
                  kind="inline"
                  isExternal
                  href={`${helpHotjarUrl}/articles/10434365668247`}
                >
                  {t('common:recordings.error-filter.read-more-link')}
                </StyledLink>
              </span>
            }
            footer={
              isScaleTrial ? (
                <FooterContainer type="info">
                  <Badge kind="custom" size="s" color={tokens.BRAND_COLORS.ember_700}>
                    {t('common:player.scale-trial')}
                  </Badge>
                  <FooterText>
                    {t('common:recordings.error-filter.footer-scale-limit-message')}
                  </FooterText>
                </FooterContainer>
              ) : null
            }
            dateConstraint={dateConstraint}
            dateRange={dateRange}
          />
        );
      }}
    />
  );
}
